import { Chip, CircularProgress, FormControl, Grid, TextField, Typography } from '@mui/material';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import {
  Customer,
  CustomerLocationOrder,
  DeliveryZoneWithLocationIds,
  ZoneRoutingInfo,
} from '../../../store/config/types';
import { deliveryOrdersService } from '../../../services/deliveryOrders.service';
import { Warehouse } from '../../../store/config/types/warehouses.types';
import PickupTimeWindowSelector from './pickup-timewindow-selector';
import FilterableTable from '../../../components/FilterableTable/FilterableTable';
import PackageGroupLink from '../../../components/PackageGroupLink/PackageGroupLink';
import { getPackageGroupLabel, getStatusOfAllPackageGroups } from '../../../utils/deliveryOrders.helpers';
import { formatTimeWindow, getCustomerDisplayName } from '../../../utils/string.helpers';
import { VehiclesService } from '../../../services/vehicles.service';
import { Check, Error, Unarchive } from '@material-ui/icons';
import { useTargetDate } from '../../../store/context/date.context';

interface TripWizardDeliverySelectorProps {
  tripDate?: string;
  shipperPickupTimeWindow?: [string, string];
  routingAttributes?: any;
}

const TripWizardDeliverySelector = ({
  shipperPickupTimeWindow,
  routingAttributes,
  tripDate,
}: TripWizardDeliverySelectorProps) => {
  const { setDate } = useTargetDate();
  const [deliveryOrdersOfDate, _setDeliveryOrdersOfDate] = useState<CustomerLocationOrder[]>([]);
  const [selectedDeliveryOrders, _setSelectedDeliveryOrders] = useState<number[]>([]);
  const [fetching, setFetching] = useState(false);
  const [packageCount, setPackageCount] = useState<number>(0);

  const setRoutingAttributes = (attribute: string, value: any) => {
    console.log(attribute, value);
    if (routingAttributes?.current) {
      routingAttributes.current[attribute] = value;
    }
  };
  const setDeliveryOrdersOfDate = (ordersOfDay: CustomerLocationOrder[]) => {
    _setDeliveryOrdersOfDate(ordersOfDay ?? []);
    if (routingAttributes?.current) {
      routingAttributes.current.deliveryOrdersOfDate = ordersOfDay;
    }
  };

  const setSelectedDeliveryOrders = (indexes: number[]) => {
    let packCount = 0;
    indexes.forEach((rowIdx) => {
      packCount += Math.ceil(
        deliveryOrdersOfDate[rowIdx]?.packageGroups?.reduce((total, p) => total + (p.packageCount ?? 0), 0) ?? 0,
      );
    });
    setPackageCount(packCount);
    setRoutingAttributes('selectedDeliveryOrders', indexes);
  };

  useEffect(() => {
    if (tripDate && tripDate !== routingAttributes?.current?.tripDate) {
      setFetching(true);
      setRoutingAttributes('tripDate', tripDate);

      const ordersOfDay: CustomerLocationOrder[] = [];
      deliveryOrdersService.fetchUnassignedOrders(tripDate, true).then((zonedDeliveryOrders) => {
        const allZones: DeliveryZoneWithLocationIds[] = [];
        const zInfos: { [key: number]: ZoneRoutingInfo } = {};
        zonedDeliveryOrders?.forEach((zone) => {
          zInfos[zone.deliveryZoneId ?? 0] = {
            packageGroupIds: [],
            selectedDeliveryServiceProviders: [],
            startsAt: '',
            vehicleIds: [],
            isRoundTrip: true,
            deliveryZoneId: zone.deliveryZoneId ?? undefined,
          };
          allZones.push({
            caption: zone.caption,
            deliveryZoneId: zone.deliveryZoneId,
            locationIds: [],
          });
          zone.orders.forEach((order) => {
            order.additionalData = {
              ...(order.additionalData ?? {}),
              zoneId: zone.deliveryZoneId,
              zoneName: zone.caption,
            };
            order.packageGroups?.forEach((p) => {
              p.packageCount = p.packages?.reduce((total, pk) => total + (pk.utilizationRatio ?? 1), 0);
            });
            ordersOfDay.push(order);
          });
        });

        setRoutingAttributes('zones', allZones);
        // setCurrentZone(allZones.length ? allZones[0].deliveryZoneId ?? 0 : 0);
        setDeliveryOrdersOfDate(ordersOfDay);
        // select all orders
        setSelectedDeliveryOrders(ordersOfDay?.map((deliveryOrder, index) => index) ?? []);
        _setSelectedDeliveryOrders(ordersOfDay?.map((deliveryOrder, index) => index) ?? []);

        const orderCustomers: Customer[] = [];
        const orderWarehouse: Warehouse[] = [];

        zonedDeliveryOrders?.forEach((zonedOrders) => {
          zonedOrders.orders.forEach((order) => {
            if (orderCustomers.findIndex((c) => c.customerId === order.customerId) < 0) {
              orderCustomers.push(order.customer);
            }
            if (orderWarehouse.findIndex((c) => c.warehouseId === order.warehouseId) < 0) {
              orderWarehouse.push(order.warehouse);
            }
          });
        });
        routingAttributes.current.warehouses = orderWarehouse;
        routingAttributes.current.customers = orderCustomers;

        if (routingAttributes?.current) {
          VehiclesService.fetchVehicles(['courier'], true).then((fetchedVehicles) => {
            if (fetchedVehicles?.length) {
              routingAttributes.current.vehicles = fetchedVehicles;
              const allVehicleIds = fetchedVehicles
                // .filter((v) => v.status === VehicleStatus.AVAILABLE)
                .map((v) => v.vehicleId ?? 0);
              allZones.forEach((zone) => {
                zInfos[zone.deliveryZoneId ?? 0].vehicleIds = [...allVehicleIds];
              });
              routingAttributes.current.zonesInfo = zInfos;

              setFetching(false);
            }
          });
        }
      });
    } else {
      _setSelectedDeliveryOrders(routingAttributes?.current?.selectedDeliveryOrders ?? []);
      setDeliveryOrdersOfDate(routingAttributes?.current?.deliveryOrdersOfDate ?? []);
    }
  }, [tripDate]);

  const changeDate = (date: string) => {
    // if (moment(date).startOf('day').isSameOrAfter(moment().startOf('day'))) {
    setDate(date);
    // }
  };

  console.log('refresh delivery selector');

  function PackagesCount() {
    if (packageCount) {
      return (
        <Grid container alignContent="center" wrap="nowrap">
          <Chip
            icon={<Unarchive style={{ height: 16 }} />}
            label={packageCount}
            variant="outlined"
            size="small"
            color="info"
          />
        </Grid>
      );
    }
    return <></>;
  }

  return (
    <Grid container flexDirection="column">
      <Grid item p={1}>
        <Typography variant="subtitle2" gutterBottom>
          Please set the trip&apos;s date and specify the time when the packages are ready.
        </Typography>
        <Grid container spacing={2} pt={2}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <TextField
                id="date"
                type="date"
                label="Date"
                variant="outlined"
                value={tripDate}
                onChange={(e) => {
                  changeDate(e.target.value);
                }}
                required
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={9}>
            <PickupTimeWindowSelector
              routingAttributes={routingAttributes ?? {}}
              shipperPickupTimeWindow={shipperPickupTimeWindow}
              date={tripDate ?? moment().format('YYYY-MM-DD')}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item flexGrow={1} position="relative">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'scroll' }}>
          {fetching ? (
            <CircularProgress size={14} />
          ) : (
            <FilterableTable
              columns={[
                { field: 'zone', caption: 'Zone', sortable: true, filterType: 'values' },
                {
                  field: 'orderId',
                  caption: 'ID',
                  sortable: true,
                  filterType: 'string',
                  render: (row) => (
                    <Grid container direction="column">
                      {row.deliveryOrder.packageGroups?.map((packageGroup: any) => (
                        <PackageGroupLink packageGroup={packageGroup} key={packageGroup.packageGroupId} />
                      ))}
                    </Grid>
                  ),
                },
                {
                  field: 'status',
                  caption: 'Status',
                  sortable: true,
                  filterType: 'values',
                  render: (row) => {
                    const isOk = `${row.status}`.toLowerCase() == 'ready to ship';
                    return (
                      <Chip
                        size="small"
                        label={row.status}
                        color={isOk ? 'success' : 'error'}
                        variant={'outlined'}
                        icon={isOk ? <Check /> : <Error />}
                      />
                    );
                  },
                },
                { field: 'customer', caption: 'Customer', sortable: true, filterType: 'string' },
                { field: 'addressee', caption: 'Addressee', sortable: true, filterType: 'string' },
                { field: 'shippingAddress', caption: 'Ship to', sortable: true, filterType: 'string' },
                {
                  field: 'timeWindow',
                  caption: 'Time window',
                  sortable: true,
                  filterType: 'string',
                  render(row) {
                    return formatTimeWindow(row.timeWindowFrom, row.timeWindowTo, true);
                  },
                },
                { field: 'packages', caption: 'Packages', sortable: true, headerInfoRender: PackagesCount },
                { field: 'warehouse', caption: 'Warehouse', sortable: true, filterType: 'values' },
              ]}
              data={
                deliveryOrdersOfDate?.map((d: CustomerLocationOrder, index: number) => {
                  let twFrom: string | undefined = '';
                  let twTo: string | undefined = '';
                  d.packageGroups.forEach((pg) => {
                    if (pg.timeWindowFrom) {
                      if (!twFrom || moment(pg.timeWindowFrom, 'HH:mm:dd').isAfter(moment(twFrom, 'HH:mm:dd'))) {
                        twFrom = pg.timeWindowFrom;
                      }
                    }
                    if (pg.timeWindowTo) {
                      if (!twTo || moment(pg.timeWindowTo, 'HH:mm:dd').isAfter(moment(twTo, 'HH:mm:dd'))) {
                        twTo = pg.timeWindowTo;
                      }
                    }
                  });
                  return {
                    id: index,
                    deliveryOrder: d,
                    orderId: d.packageGroups?.reduce((str, p) => {
                      return str + ' ' + getPackageGroupLabel(p);
                    }, ''),
                    customer: getCustomerDisplayName(d.customer),
                    packages: d.packageCount,
                    shippingAddress: d.shippingLocation?.streetAddress,
                    addressee: d.shipToAddressee,
                    warehouse: d.warehouse?.name,
                    shipVia: d.shipVia == 'DEFAULT' ? 'Curbinity' : d.shipVia,
                    status: getStatusOfAllPackageGroups(d),
                    zone: d.additionalData.zoneName,
                    timeWindow: formatTimeWindow(twFrom, twTo, false, true),
                    timeWindowFrom: twFrom,
                    timeWindowTo: twTo,
                  };
                }) ?? []
              }
              itemPerPage={20}
              externalFilterValues={{}}
              selectable
              selectedIds={selectedDeliveryOrders}
              onSelectionChange={setSelectedDeliveryOrders}
              primaryKey="id"
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default React.memo(TripWizardDeliverySelector, (prevProps, nextProps) => {
  if (prevProps.tripDate != nextProps.tripDate) return false;
  return true;
});
